// Unified API client - single source of truth for all API calls
const API_BASE_URL = (import.meta.env.VITE_API_URL || 'http://localhost:8080').replace(/\/$/, '');

/**
 * Error thrown by the API client for non-2xx HTTP responses. Preserves the HTTP status
 * code and parsed response body so callers can distinguish status-specific handling
 * (e.g. 409 conflict path in `useEditAppeal`) without parsing the message string.
 */
export class ApiError extends Error {
  status: number;
  data: any;
  constructor(message: string, status: number, data: any) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
    Object.setPrototypeOf(this, ApiError.prototype);
  }
}
/** Max retries for refresh on transient errors (e.g. network) */
const REFRESH_MAX_RETRIES = 2;
/** Delay in ms before retry */
const REFRESH_RETRY_DELAY_MS = 1000;

/** In-flight refresh promise so concurrent requests share one refresh call */
let refreshPromise: Promise<string | null> | null = null;

// No need for endpoints object - use strings directly

// Simple token management
const getAccessToken = (): string | null => {
  return localStorage.getItem('idToken');
};

const isTokenExpired = (): boolean => {
  const expiredAt = localStorage.getItem('expiredAt');
  if (!expiredAt) return true;
  return Date.now() >= parseInt(expiredAt);
};

const clearTokensAndRedirect = (): void => {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('idToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('expiredAt');
  localStorage.removeItem('user');
  window.location.href = '/login';
};

/** True if error is network/transient — retry or throw, do NOT logout */
const isNetworkOrTransientError = (response: Response | null): boolean => {
  if (response !== null) {
    if (response.status === 400 || (response.status >= 401 && response.status < 500)) return false; // permanent auth failure
    return response.status >= 500 || response.status === 0; // 5xx or network
  }

  return true; // fetch threw (e.g. network) → treat as transient
};

const refreshAccessToken = async (retryCount = 0): Promise<string | null> => {
  const refreshToken = localStorage.getItem('refreshToken');
  if (!refreshToken) return null;

  const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refreshToken }),
  });

  if (!response.ok) {
    const transient = isNetworkOrTransientError(response);
    const canRetry = retryCount < REFRESH_MAX_RETRIES && transient;
    if (canRetry) {
      await new Promise(r => setTimeout(r, REFRESH_RETRY_DELAY_MS));
      return refreshAccessToken(retryCount + 1);
    }
    // Permanent auth failure (e.g. 400) → logout
    if (!transient) {
      clearTokensAndRedirect();
      return null;
    }
    // Transient but no retries left → throw, do NOT logout
    throw new Error('Refresh failed - please check your connection and try again');
  }

  const data = await response.json();
  localStorage.setItem('accessToken', data.accessToken);
  localStorage.setItem('expiredAt', data.expiredAt);
  localStorage.setItem('idToken', data.idToken);

  return data.idToken;
};

/** Single in-flight refresh so concurrent requests don't each trigger refresh */
const refreshAccessTokenWithDedup = async (): Promise<string | null> => {
  if (refreshPromise) return refreshPromise;
  refreshPromise = refreshAccessToken().finally(() => {
    refreshPromise = null;
  });

  return refreshPromise;
};

// Main API request function
async function apiRequest<T>(
  endpoint: string,
  options: RequestInit & { skipAuth?: boolean } = {}
): Promise<T> {
  const { skipAuth = false, ...fetchOptions } = options;

  const url = endpoint.startsWith('http')
    ? endpoint
    : `${API_BASE_URL}${endpoint.startsWith('/') ? endpoint : `/${endpoint}`}`;

  // Don't set Content-Type for FormData - let browser set it automatically
  const isFormData = fetchOptions.body instanceof FormData;
  const headers: Record<string, string> = {
    ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
    ...(fetchOptions.headers as Record<string, string>),
  };

  // Add auth token if not skipped and token exists
  if (!skipAuth) {
    let token = getAccessToken();

    // Try to refresh token if expired (single in-flight refresh for concurrent requests)
    if (token && isTokenExpired()) {
      token = await refreshAccessTokenWithDedup();
    }

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
  }

  let response = await fetch(url, {
    ...fetchOptions,
    headers,
  });

  // On 401, try refresh once and retry the request before giving up.
  // If refreshAccessTokenWithDedup throws (e.g. network error), the error propagates — we do NOT logout.
  if (response.status === 401 && !skipAuth) {
    const newToken = await refreshAccessTokenWithDedup();
    if (newToken) {
      const retryHeaders: Record<string, string> = {
        ...(fetchOptions.headers as Record<string, string>),
        Authorization: `Bearer ${newToken}`,
      };
      if (!(fetchOptions.body instanceof FormData)) {
        retryHeaders['Content-Type'] = 'application/json';
      }
      response = await fetch(url, {
        ...fetchOptions,
        headers: retryHeaders,
      });
    }
    if (response.status === 401) {
      clearTokensAndRedirect();
      throw new Error('Unauthorized - redirecting to login');
    }
  }

  try {
    if (!response.ok) {
      const errorText = await response.text();
      let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
      let errorJson: any = null;

      try {
        errorJson = JSON.parse(errorText);
        if (typeof errorJson.message === 'object') {
          errorMessage = JSON.stringify(errorJson.message);
        } else {
          errorMessage = errorJson.message || errorMessage;
        }
        console.error('API Error Details:', errorJson);
        console.error('Parsed error message:', errorMessage);
        console.error('Full error response:', errorText);
      } catch {
        console.error('API Error (non-JSON):', errorText);
        // Use default error message if response is not JSON
      }

      throw new ApiError(errorMessage, response.status, errorJson);
    }

    // Handle different response types
    const contentType = response.headers.get('content-type');
    if (contentType?.includes('application/json')) {
      return await response.json();
    } else if (contentType?.includes('text/')) {
      return (await response.text()) as T;
    } else {
      return (await response.blob()) as T;
    }
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    console.error(`API request failed: ${endpoint}`, errorMessage);
    throw error;
  }
}

// Convenience methods
export const api = {
  get: <T>(endpoint: string, options?: RequestInit & { skipAuth?: boolean }) =>
    apiRequest<T>(endpoint, { ...options, method: 'GET' }),

  post: <T>(endpoint: string, data?: any, options?: RequestInit & { skipAuth?: boolean }) =>
    apiRequest<T>(endpoint, {
      ...options,
      method: 'POST',
      body: data instanceof FormData ? data : data ? JSON.stringify(data) : undefined,
    }),

  put: <T>(endpoint: string, data?: any, options?: RequestInit & { skipAuth?: boolean }) =>
    apiRequest<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: data instanceof FormData ? data : data ? JSON.stringify(data) : undefined,
    }),

  patch: <T>(endpoint: string, data?: any, options?: RequestInit & { skipAuth?: boolean }) =>
    apiRequest<T>(endpoint, {
      ...options,
      method: 'PATCH',
      body: data instanceof FormData ? data : data ? JSON.stringify(data) : undefined,
    }),

  delete: <T>(endpoint: string, options?: RequestInit & { skipAuth?: boolean }) =>
    apiRequest<T>(endpoint, { ...options, method: 'DELETE' }),
};

export default api;
